// @owners { team: patients }
import { SPACING } from '@alto/design-library-tokens';
import {
  ButtonGroup,
  type ButtonGroupProps,
  type Description,
  LgSpacing,
  NavBarLogo,
  Row,
  SecondaryPage,
  SmSpacing,
  XsSpacing,
} from '@alto/design-system';
import React, { type ComponentProps } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { progynyAssets } from './progynyAssets';
import { getProgynyPageBackgroundColor } from './progynyBackgroundColor';

type SecondaryPageProps = ComponentProps<typeof SecondaryPage>;

type Props = Omit<SecondaryPageProps, 'buttons' | 'Footnote' | 'inlineButtons' | 'justifyButtons'> & {
  readonly buttons?: ButtonGroupProps['buttons'];
  readonly Footnote?: React.ReactElement<typeof Description>;
  readonly justifyButtons?: ButtonGroupProps['justifyButtons'];
  readonly inlineButtons?: ButtonGroupProps['inline'];
};

const IS_WEB = Platform.OS === 'web';
// flex:1 so SecondaryPage's flex-grow PageRoot still fills the screen.
const styles = StyleSheet.create({ fill: { flex: 1 } });

export const ProgynyPage = ({
  buttons,
  Footnote,
  justifyButtons,
  inlineButtons = false,
  headerBackgroundColor = getProgynyPageBackgroundColor(),
  children,
  HeaderContent,
  ...rest
}: Props) => {
  const hasButtons = !!buttons && buttons.length > 0;
  const { top } = useSafeAreaInsets();

  // The NavBar is transparent and reserves no band, so the Progyny logo rises to the top of the
  // page with the back chevron floating over it. The transparent NavBar also drops the safe-area
  // inset the band used to carry, so re-add just that inset above the logo: on native it's the
  // status-bar height (so the logo isn't clipped under the notch / status bar); on web there's no
  // safe area, so use a small fixed inset to keep the logo off the very top edge.
  const topInsetStyle = { height: IS_WEB ? SPACING.STATIC.MD.value : top };

  // Progyny screens render the Progyny logo centered at the top of the page body (both platforms),
  // above the screen's own header content. The logo is no longer shown in the NavBar, and on web
  // the global Alto header is hidden for Progyny routes (see useIsProgynyRoute / DashboardNavbar).
  const headerContent = (
    <>
      <View style={topInsetStyle} />
      <Row center>
        <NavBarLogo source={progynyAssets.logo} />
      </Row>
      <SmSpacing />
      {HeaderContent}
    </>
  );

  // On native, render SecondaryPage directly (no wrapper) with its `buttons` prop, so the
  // buttons stay anchored at the bottom via AnchoredButtonGroup — identical to the layout
  // before this wrapper existed. The View wrapper and inline-buttons logic below are only
  // applied on web, where the AnchoredButtonGroup gets hidden behind the apps/web page footer.
  if (!IS_WEB) {
    return (
      <SecondaryPage
        {...rest}
        headerBackgroundColor={headerBackgroundColor}
        transparentNavBar
        HeaderContent={headerContent}
        buttons={hasButtons ? buttons : undefined}
        Footnote={Footnote}
        justifyButtons={justifyButtons}
        inlineButtons={inlineButtons}
      >
        {children}
      </SecondaryPage>
    );
  }

  return (
    <View style={[styles.fill, { backgroundColor: headerBackgroundColor }]}>
      <SecondaryPage
        {...rest}
        headerBackgroundColor={headerBackgroundColor}
        transparentNavBar
        // The global Alto web header is hidden on Progyny routes (see useIsProgynyRoute), so size the
        // page to the footer without reserving the nav bar height or the anchored-button margin —
        // otherwise there's dead space between the content and the footer.
        webNavBarHidden
        HeaderContent={headerContent}
      >
        {children}
        {Footnote ? (
          <>
            <LgSpacing />
            <Row center>{Footnote}</Row>
            <XsSpacing />
          </>
        ) : null}
        {hasButtons ? (
          <>
            {!Footnote ? <LgSpacing /> : null}
            <ButtonGroup
              rowReversed
              buttons={buttons}
              inline={inlineButtons}
              justifyButtons={justifyButtons}
            />
          </>
        ) : null}
      </SecondaryPage>
    </View>
  );
};
